import { LWModal, Table, useOnScrollDown } from 'redesign';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';
import { RenderUnsettledUI } from 'shared/components';
import { Box } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetShiftPlanningShifts } from './hooks/use-get-shift-planning-shifts';
import { getShiftPlanningTableData } from './utils/get-shift-planning-table-data';
import { getSelectedRows } from './utils/get-selected-row-ids';
import {
  HqShiftPlanningApplicationColumnDefinition,
  HqShiftPlanningTableOptions,
  INITIAL_PAGE,
  ShiftPlanningTableProps,
} from './shift-planning-table.types';
import { useShiftPlanningPage } from './hooks/use-shift-planning';
import { getShiftPlanningApplicationColumnDefinition } from './utils/get-shift-planning-column-definition';
import { ShiftPlanningModalProvider } from '../shift-planning-modal/shift-planning-modal';
import { ShiftActionCallback } from '../hooks/use-get-shift-detail-head-actions';

type ShiftPlanningTableDataProviderProps = {
  onSelectionChange: (selectedIds: string[]) => void;
  selectedShiftIds: string[];
  setShiftAction: ShiftActionCallback;
};

export const ShiftPlanningTableDataProvider: FC<ShiftPlanningTableDataProviderProps> = ({
  onSelectionChange,
  selectedShiftIds,
  setShiftAction,
}) => {
  const { filters: appliedFilters } = useShiftPlanningPage();

  const shiftsQuery = useGetShiftPlanningShifts({
    fromDate: appliedFilters.fromDate ?? undefined,
    company: appliedFilters.company ?? undefined,
    branchOfficeIds: appliedFilters.branchOfficeIds ?? undefined,
    departmentIds: appliedFilters.departmentIds ?? undefined,
    jobType: appliedFilters.jobType ?? undefined,
    filledStatus: appliedFilters.filledStatus ?? undefined,
    partner: appliedFilters.partner ?? undefined,
    isFreelance: appliedFilters.isFreelance ?? undefined,
    isTempWork: appliedFilters.isTempWork ?? undefined,
    areFreelanceApplications: appliedFilters.areFreelanceApplications ?? undefined,
    areTempworkApplications: appliedFilters.areTempworkApplications ?? undefined,
    areNoApplications: appliedFilters.areNoApplications ?? undefined,
    pageParam: INITIAL_PAGE,
    pageSizeParam: DEFAULT_PAGE_SIZE,
  });

  if (shiftsQuery.status !== 'success') {
    return <RenderUnsettledUI data={shiftsQuery} />;
  }

  return (
    <ShiftPlanningTable
      shiftsQuery={shiftsQuery}
      selectedShiftIds={selectedShiftIds}
      onSelectionChange={onSelectionChange}
      setShiftAction={setShiftAction}
    />
  );
};

const ShiftPlanningTable = ({
  shiftsQuery,
  onSelectionChange,
  selectedShiftIds,
  setShiftAction,
}: ShiftPlanningTableProps) => {
  const { data } = shiftsQuery;
  const { t } = useTranslation('hq-screen-specific');
  const { setFilters, shiftDetailId, setShiftDetailId } = useShiftPlanningPage();
  const flatData = useMemo(() => data.pages.flatMap((page) => page.items), [data]);
  const tableData = useMemo(() => getShiftPlanningTableData(flatData), [flatData]);
  const columns = useMemo(() => getShiftPlanningApplicationColumnDefinition(t), []);
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: shiftsQuery,
    flatData: flatData,
  });
  const selectedRows = getSelectedRows(flatData, selectedShiftIds);

  const handleSelectionChange = useCallback(
    (rows: HqShiftPlanningApplicationColumnDefinition[]) => {
      const selectedIds = rows.map((row) => row.id);
      onSelectionChange(selectedIds);
    },
    [onSelectionChange]
  );

  const openShiftDetail = useCallback(
    (shiftId: string) => {
      setFilters({ shiftDetailId: shiftId });
      setShiftDetailId(shiftId);
    },
    [setFilters]
  );

  const closeShiftDetail = useCallback(() => {
    setFilters({ shiftDetailId: undefined });
    setShiftDetailId(undefined);
  }, [setFilters]);

  if (shiftsQuery.status !== 'success') {
    return <RenderUnsettledUI data={shiftsQuery} />;
  }

  return (
    <>
      <Box minHeight={0}>
        <Table<HqShiftPlanningApplicationColumnDefinition, HqShiftPlanningTableOptions>
          stickyHeader={true}
          columns={columns}
          data={tableData}
          tableStyle={{ tableLayout: 'fixed' }}
          onScroll={fetchIfBottomScrolled}
          onShowDetail={openShiftDetail}
          data-testid="shift-planning-virtual-table"
          onSelectionChange={handleSelectionChange}
          selectable={true}
          selectedRows={selectedRows}
        />
      </Box>
      <LWModal isOpen={shiftDetailId != null} onClose={closeShiftDetail} testId="shift-detail">
        {shiftDetailId ? (
          <ShiftPlanningModalProvider
            shiftDetailId={shiftDetailId}
            onDelete={shiftsQuery.refetch}
            closeShiftDetail={closeShiftDetail}
            setShiftAction={setShiftAction}
          />
        ) : null}
      </LWModal>
    </>
  );
};
